import type { TransactionResponse } from "@ethersproject/abstract-provider"
import { useEthers } from "@usedapp/core"
import type { ErrorContextType } from "context/error"
import { ErrorContext } from "context/error"
import { useCallback, useContext } from "react"

import { ERROR_MODAL_TYPE } from "components/modals/error-modal"

import { extractContractError } from "utils/extract-contract-error"
import { extractErrorMessage } from "utils/extract-error-message"

import type { TransactionReceipt } from "types/transactions"

const TRANSACTION_FAILED_TITLE = "Transaction Failed."
const BLOCKCHAIN_CONNECTION_FAILED = "Blockchain Connection failed. Try Again or contact us."

export function useErrorModal() {
  const context = useContext<ErrorContextType | null>(ErrorContext)
  const { library } = useEthers()

  const handleTransaction = useCallback(
    async (cb: Promise<(TransactionReceipt | TransactionResponse) & { hash: string }>) => {
      let hash
      try {
        if (!library) throw new Error(BLOCKCHAIN_CONNECTION_FAILED)
        const tr = await cb
        const result = await library?.waitForTransaction(tr?.hash)

        if (result?.status === 0) {
          hash = result?.transactionHash
          throw new Error(TRANSACTION_FAILED_TITLE)
        }

        return result
      } catch (error) {
        const { title, content } = extractContractError(error, ERROR_MODAL_TYPE.TRANSACTION)
        context?.setError({ text: content, title, hash })
        throw new Error(extractErrorMessage(error))
      }
    },
    [context, library],
  )

  return {
    ...context,
    handleTransaction,
  }
}
